import { Component, OnInit, Input, Output, EventEmitter, OnChanges } from '@angular/core';
import { FormGroup, FormControl, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { TipoDenunciaService } from 'src/app/dashboard/services/tipo-denuncia.service';
import { ReceptorObligatorio } from 'src/app/dashboard/models/receptorobligaorio';
import { AuthService } from 'src/app/shared/services/auth.service';
import { CustomValidators } from 'src/app/shared/custom-validator/custom-validator.component';
import { ApiService } from 'src/app/shared/services/api.service';
import { Abogado } from 'src/app/model/abogado';

/**
 * Enum that represents how the complaint type is managed.
 */
export enum TipoGestion {
  SinGestor = '0',
  Usuario = '1',
  Abogado = '2'
}

@Component({
  selector: 'app-add-tipo-denuncia',
  templateUrl: './add-tipo-denuncia.component.html',
  styleUrls: ['./add-tipo-denuncia.component.css']
})
export class AddTipoDenunciaComponent implements OnInit, OnChanges {

  /** Complaint type received from parent component when editing */
  @Input() tipoToEdit: any = null;

  /** Indicates whether the component is used inside another view */
  @Input() embedded = false;

  /** Emits when the modal/component must be closed */
  @Output() close = new EventEmitter<void>();

  /** Emits when the complaint type is successfully saved */
  @Output() saved = new EventEmitter<any>();

  /** Flag to know if we are editing or creating */
  isEdit = false;

  /** Reactive form instance */
  validatingForm: FormGroup;

  /** List of mandatory receivers (managers) */
  gestores: ReceptorObligatorio[] = [];

  /** List of lawyers available for the society */
  abogados: Abogado[] = [];

  /** Option value for no receiver */
  sinReceptorOption = 'Sin receptor';

  constructor(
    private tipoDenunciaService: TipoDenunciaService,
    private auth: AuthService,
    private apiService: ApiService,
    private toastr: ToastrService,
    private translate: TranslateService
  ) { }

  /**
   * Initializes language, form and catalogs
   */
  ngOnInit(): void {
    const language = this.auth.getLanguage() || 'es';
    this.translate.setDefaultLang(language);



    this.buildForm();
    this.loadCatalogs();

 /**
 * Synchronizes the conflict manager based on the receptor manager's value changes.
 * Handles the enabling, disabling, and value resetting logic for 'gestorConflicto'.
 */
this.gestor.valueChanges.subscribe(value => {
  // If no receptor management is present, disable the conflict manager
  if (!value) {
    this.gestorConflicto.reset(null, { emitEvent: false });
    this.gestorConflicto.disable({ emitEvent: false });
    return;
  }

  if (value && value.nombre === this.sinReceptorOption) {
    // Automatically copy value and disable if it matches the 'no receptor' translation
    this.gestorConflicto.setValue(value);


    this.gestorConflicto.disable({ emitEvent: false });
  } else {
    // Enable and reset if it's a valid, specific receptor
    this.gestorConflicto.enable({ emitEvent: false });
    this.gestorConflicto.reset(null, { emitEvent: false });
  }
});

    // Apply data if the parent already sent an item
    this.applyTipoToEdit();
  }

  /**
   * Reacts to input changes (useful when data arrives after init)
   */
  ngOnChanges(): void {
    if (this.validatingForm) {
      this.applyTipoToEdit();
    }
  }

  /**
   * Applies the received complaint type to the form when editing
   */
  private applyTipoToEdit() {
    if (this.tipoToEdit) {
      this.isEdit = true;

      // Resolve gestor object (the select expects the full object)
      let gestorObj: any = null;
      if (
        this.tipoToEdit.gestor &&
        typeof this.tipoToEdit.gestor === 'object' &&
        (this.tipoToEdit.gestor.id || this.tipoToEdit.gestor.nombre)
      ) {
        gestorObj = this.tipoToEdit.gestor;
      } else {
        const gid = (this.tipoToEdit.gestor || this.tipoToEdit.id_gestor || '').toString();
        if (gid && this.gestores?.length) {
          gestorObj = this.gestores.find(g => (g.id + '') === gid) || null;
        }
      }

      // Resolve conflict manager
      let gestorConfObj: any = null;
      const gconfId = (this.tipoToEdit.id_gestor_conflicto || this.tipoToEdit.gestorConflicto || '').toString();
      if (gconfId && this.gestores?.length) {
        gestorConfObj = this.gestores.find(g => (g.id + '') === gconfId) || null;
      }

      // Patch form values
      this.validatingForm.patchValue({
        complaintTypeId: this.tipoToEdit.id_tp_denuncia || this.tipoToEdit.id || '',
        complaintTypeName: this.tipoToEdit.nombre || '',
        complaintTypeDescription: this.tipoToEdit.descripcion || '',
        complaintTypeOrder: this.tipoToEdit.orden || null,
        gestor: gestorObj,
        gestorConflicto: gestorConfObj
      });
    } else {
      this.isEdit = false;
      this.validatingForm.reset();
    }
  }

  /**
   * Builds the reactive form with validations
   */
  buildForm() {
    this.validatingForm = new FormGroup({

      complaintTypeId: new FormControl('', [
        Validators.required,
        Validators.pattern(/^[a-zA-Z0-9_-]{1,5}$/),
        CustomValidators.secureInput
      ]),
      complaintTypeName: new FormControl('', [
        Validators.required,
        Validators.maxLength(255),
        CustomValidators.secureInput
      ]),
      complaintTypeOrder: new FormControl('', [
        Validators.pattern(/^\d+$/),
        CustomValidators.secureInput
      ]),
      complaintTypeDescription: new FormControl('', [
        Validators.maxLength(4000),
        CustomValidators.secureInput
      ]),
      gestor: new FormControl(null),
      gestorConflicto: new FormControl(null)
    });
  }

  /**
   * Loads catalogs such as lawyers and managers
   */
  loadCatalogs() {
    this.apiService
      .searchLayerByUserSociety(this.auth.getSociedad())
      .subscribe(res => this.abogados = res.data || []);

    this.tipoDenunciaService
      .searchReceptorObligatoryByUserSociety(
        this.auth.getIdUser(),
        this.auth.getSociedad()
      )
      .subscribe(res => {
        this.gestores = res.data || [];
        this.applyTipoToEdit();
      });
  }


  /**
   * Creates or updates a complaint type
   */
  save() {
    if (this.validatingForm.invalid) return;

    const form = this.validatingForm.value;
    const language = this.auth.getLanguage() || 'es';

    let gestor = '';
    let tipoGestion = TipoGestion.SinGestor;

    // Resolve gestor and management type (object or id)
    if (form.gestor) {
      if (typeof form.gestor === 'object') {
        tipoGestion = form.gestor.tipo_receptor || TipoGestion.SinGestor;
        gestor = form.gestor.id || '';
      } else {
        const gid = (form.gestor + '').toString();
        const gObj = this.gestores?.find(g => (g.id + '') === gid);
        if (gObj) {
          tipoGestion = (gObj.tipo_receptor as TipoGestion) || TipoGestion.SinGestor;
          gestor = gObj.id || gid;
        } else {
          gestor = gid;
        }
      }

      if (!gestor) {
        gestor = this.sinReceptorOption;
      }
    } else {
      gestor = this.sinReceptorOption;
      tipoGestion = TipoGestion.SinGestor;
    }

    // Resolve conflict manager
    let gestorConf = '';
    if (form.gestorConflicto) {
      if (typeof form.gestorConflicto === 'object') {
        gestorConf = form.gestorConflicto.id || form.gestorConflicto.nombre || '';
      } else {
        gestorConf = (form.gestorConflicto + '').toString();
      }
    }
    if (!gestorConf) {
      gestorConf = this.sinReceptorOption;
    }

    // Default description if empty
    let description = form.complaintTypeDescription?.trim();

    // Update or create logic
    if (this.isEdit) {
      this.tipoDenunciaService.updateComplaintByUser(
        this.auth.getSociedad(),
        form.complaintTypeId,
        form.complaintTypeName,
        description,
        this.auth.getIdUser(),
        tipoGestion,
        gestor,
        language,
        gestorConf,
        form.complaintTypeOrder,
        form.complaintTypeVisible ?? 1, // preserve current visibility, default to visible
      ).subscribe({
        next: res => {
          if (res.success && res.code === 200) {
            this.toastr.success(
              this.translate.instant('texto_236'),
              this.translate.instant('texto_238')
            );
            this.saved.emit(res);
          } else {
            this.toastr.error(
              this.translate.instant('texto_237'),
              this.translate.instant('texto_813')
            );
          }
        },
        error: error => {
          if (error.error?.code === 409 && error.error?.message === 'Duplicate entry') {
            this.toastr.error(
              this.translate.instant('texto_939'),
              this.translate.instant('texto_813')
            );
          } else {
            this.toastr.error(
              this.translate.instant('texto_237'),
              this.translate.instant('texto_813')
            );
          }
        }
      });
    } else {
      const rawForm = this.validatingForm.getRawValue();

      this.tipoDenunciaService.addTypeComplaint(
        this.auth.getSociedad(),
        form.complaintTypeId, // Using the same value for id and group as per backend requirements
        form.complaintTypeName,
        description,
        tipoGestion,
        gestor,
        language,
        gestorConf,
        form.complaintTypeOrder
      ).subscribe({
        next: res => {
          if (res.success && res.code === 200) {
            this.toastr.success(
              this.translate.instant('texto_317'),
              this.translate.instant('texto_319')
            );
            this.saved.emit(res);
          } else {
            this.toastr.error(
              this.translate.instant('texto_318'),
              this.translate.instant('texto_813')
            );
          }
        },
        error: error => {
          if (error.error.code === 409 && error.error.errors[0] === 'A complaint type with the same id/idioma/sociedad already exists.') {
            this.toastr.error(
              this.translate.instant('texto_939'),
              this.translate.instant('texto_813')
            );
          } else {
            this.toastr.error(
              this.translate.instant('texto_318'),
              this.translate.instant('texto_813')
            );
          }
        }
      });
    }
  }

  /**
   * Handles close behavior depending on usage context
   */
  onCloseClicked(frame?: any) {
    if (this.embedded) {
      this.close.emit();
    } else if (frame?.hide) {
      frame.hide();
    } else {
      this.close.emit();
    }
  }

  /** Form getters for template access */
  get complaintTypeName() {
    return this.validatingForm.get('complaintTypeName');
  }

  get complaintTypeId() {
    return this.validatingForm.get('complaintTypeId');
  }

  get complaintTypeOrder() {
    return this.validatingForm.get('complaintTypeOrder');
  }

  get complaintTypeDescription() {
    return this.validatingForm.get('complaintTypeDescription');
  }

  get gestor() {
    return this.validatingForm.get('gestor');
  }

  get gestorConflicto() {
    return this.validatingForm.get('gestorConflicto');
  }

}

